import React, {useEffect, useState} from 'react';
import {Button, Drawer, Input, message, Modal, Spin, Table} from 'antd';
import {CloseOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {useMutation, useQuery} from '@apollo/client';
import {
    GET_APPOINTMENT_PARCELS,
    GetAppointmentParcelsResponse,
    GetAppointmentParcelsVariables,
    ParcelItem
} from '../graphql/queries/getAppointmentParcels';
import {UPDATE_APPOINTMENT_TRACKING_NUMBERS} from '../graphql/mutations/updateAppointmentTrackingNumbers';
import {GET_PICKUP_APPOINTMENT_DETAIL} from '../graphql/queries/getPickupAppointmentDetail';
import AddParcelsModal from './AddParcelsModal';
import type {ColumnsType} from 'antd/es/table';
import {formatDate, formatISODateTime} from '../../../../utils/dateTimeUtils';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import { useTranslation } from 'react-i18next';
import { getUserInfo } from '../../../../utils/auth';

interface AppointmentParcelsDrawerProps {
    visible: boolean;
    onClose: () => void;
    appointmentId: number;
    appointmentNumber?: string;
    initialStatus?: string;
}

const PickupAppointmentParcelsDrawer: React.FC<AppointmentParcelsDrawerProps> = ({
                                                                                     visible,
                                                                                     onClose,
                                                                                     appointmentId,
                                                                                     appointmentNumber,
                                                                                     initialStatus
                                                                                 }) => {
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [filteredParcels, setFilteredParcels] = useState<ParcelItem[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ParcelItem[]>([]);
    const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
    const [addParcelsModalVisible, setAddParcelsModalVisible] = useState(false);
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');
    // 获取当前仓库
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id;
    const {t} = useTranslation();
    const {data, loading, error, refetch} = useQuery<GetAppointmentParcelsResponse, GetAppointmentParcelsVariables>(
        GET_APPOINTMENT_PARCELS,
        {
            variables: {id: appointmentId},
            skip: !visible || !appointmentId,
            fetchPolicy: 'network-only'
        }
    );

    // 查询预约单的最新状态
    const {data: appointmentDetailData} = useQuery(GET_PICKUP_APPOINTMENT_DETAIL, {
        variables: {id: appointmentId},
        skip: !visible || !appointmentId,
        fetchPolicy: 'network-only'
    });

    // 获取最新的预约单状态
    const currentStatus = appointmentDetailData?.getPickupAppointmentDetail?.status || initialStatus;

    const [updateAppointmentTrackingNumbers, {loading: updateLoading}] = useMutation(UPDATE_APPOINTMENT_TRACKING_NUMBERS);
    useEffect(() => {
        if (data?.getAppointmentParcels) {
            let parcels = [...data.getAppointmentParcels];

            if (searchText) {
                parcels = parcels.filter((p) => p.trackingNumber.toLowerCase().includes(searchText.toLowerCase()));
            }

            if (statusFilter) {
                parcels = parcels.filter((p) => p.overallStatus === statusFilter);
            }

            setFilteredParcels(parcels);
            setCurrentPage(1);
        } else {
            setFilteredParcels([]);
        }
    }, [data, searchText, statusFilter]);

    // 重置选中状态当抽屉关闭时
    useEffect(() => {
        if (!visible) {
            setSelectedRowKeys([]);
            setSelectedRows([]);
        }
    }, [visible]);

    const statusOptions = data?.getAppointmentParcels
        ? Array.from(new Set(data.getAppointmentParcels.map(p => p.overallStatus))).map(status => ({
            text: status.replace(/_/g, ' '),
            value: status
        }))
        : [];

    // 删除选中的包裹
    const handleDeleteParcels = async () => {
        if (selectedRowKeys.length === 0) return;

        try {
            // 获取选中的tracking numbers
            const trackingNumbersToRemove = selectedRowKeys.map(key => key as string);
            
            // 获取当前的追踪号列表
            const currentTrackingNumbers = data?.getAppointmentParcels?.map(p => p.trackingNumber) || [];
            
            // 计算剩余的追踪号列表
            const remainingTrackingNumbers = currentTrackingNumbers.filter(trackingNumber =>
                !trackingNumbersToRemove.includes(trackingNumber)
            );

            // 调用更新追踪号接口
            await updateAppointmentTrackingNumbers({
                variables: {
                    appointmentId: appointmentId,
                    trackingNumbers: remainingTrackingNumbers
                }
            });

            message.success(`Successfully removed ${selectedRowKeys.length} parcel(s)`);

            // 重新获取数据
            await refetch();

            // 清空选中状态
            setSelectedRowKeys([]);
            setSelectedRows([]);

        } catch (error) {
            console.error('Remove parcels error:', error);
            message.error('Failed to remove parcels');
        }
    };

    // 显示删除确认弹框
    const showDeleteConfirm = () => {
        if (selectedRowKeys.length === 0) return;
        setDeleteConfirmVisible(true);
    };

    // 处理点击tracking number
    const handleTrackingNumberClick = (parcel: ParcelItem) => {
        if (parcel.id) {
            setSelectedOrderId(parcel.id.toString());
            setParcelDetailVisible(true);
        }
    };

    // 处理删除确认
    const handleDeleteConfirm = async () => {
        await handleDeleteParcels();
        setDeleteConfirmVisible(false);
    };

    const columns: ColumnsType<ParcelItem> = [
        {
            title: t('pickup.appointment.parcelPageDetails.no'),
            key: 'index',
            width: '10%',  // 调整宽度
            align: 'left',
            render: (_, __, index) => (currentPage - 1) * 10 + index + 1,
        },
        {
            title: (
                <div className="flex items-center">
                    <span>{t('pickup.appointment.parcelPageDetails.parcel')}</span>
                </div>
            ),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            width: '45%', // 调整宽度
            align: 'left',
            filterDropdown: () => (
                <div className="p-2">
                    <Input
                        placeholder="Search parcel"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        autoFocus
                        className="w-full"
                    />
                </div>
            ),
            render: (text, record) => (
                <a 
                    className="text-purple-600 cursor-pointer hover:text-purple-800" 
                    onClick={() => handleTrackingNumberClick(record)}
                >
                    {text}
                </a>
            ),
        },
        {
            title: (
                <div className="flex items-center justify-between">
                    <span>{t('pickup.appointment.parcelPageDetails.status')}</span>
                </div>
            ),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            width: '20%', // 保持宽度
            filters: statusOptions,
            onFilter: (value, record) => record.overallStatus === value,
            render: (text) => <a className="text-purple-600">{text}</a>,
        },
        {
            title: t('pickup.appointment.parcelPageDetails.pickupScheduledDate'),
            dataIndex: 'pickupScheduledDate',
            key: 'pickupScheduledDate',
            width: '25%', // 调整宽度
            align: 'left',
            render: (text) => text ? formatDate(text) : '-',
        },
        {
            title: t('pickup.appointment.parcelPageDetails.createdTime'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: '25%', // 调整宽度
            align: 'left',
            render: (text) => text ? formatISODateTime(text) : '-',
        },
    ];


    const displayAppointmentNumber = appointmentNumber;
    const parcelCount = data?.getAppointmentParcels?.length || 0;

    // 多选配置
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: ParcelItem[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
            setSelectedRows(newSelectedRows);
        },
        onSelectAll: (selected: boolean, selectedRows: ParcelItem[], changeRows: ParcelItem[]) => {
            if (selected) {
                // 全选当前页
                const currentPageKeys = filteredParcels.slice((currentPage - 1) * 10, currentPage * 10).map(item => item.trackingNumber);
                const existingKeys = selectedRowKeys.filter(key => !currentPageKeys.includes(key as string));
                const newSelectedKeys = [...existingKeys, ...currentPageKeys];
                const newSelectedRows = [...selectedRows];

                changeRows.forEach(row => {
                    if (!newSelectedRows.find(r => r.trackingNumber === row.trackingNumber)) {
                        newSelectedRows.push(row);
                    }
                });

                setSelectedRowKeys(newSelectedKeys);
                setSelectedRows(newSelectedRows);
            } else {
                // 取消选择当前页
                const currentPageKeys = filteredParcels.slice((currentPage - 1) * 10, currentPage * 10).map(item => item.trackingNumber);
                const newSelectedKeys = selectedRowKeys.filter(key => !currentPageKeys.includes(key as string));
                const newSelectedRows = selectedRows.filter(row => !currentPageKeys.includes(row.trackingNumber));

                setSelectedRowKeys(newSelectedKeys);
                setSelectedRows(newSelectedRows);
            }
        },
        getCheckboxProps: (record: ParcelItem) => ({
            name: record.trackingNumber,
        }),
    };

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <div className="h-6 w-1 bg-purple-600 mr-3"></div>
                        <span className="text-xl font-medium">{t('pickup.appointment.parcelPageDetails.title')}</span>
                    </div>
                    <CloseOutlined onClick={onClose} className="cursor-pointer"/>
                </div>
            }
            width={800}
            placement="right"
            closable={false}
            onClose={onClose}
            open={visible}
        >
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large"/>
                </div>
            ) : error ? (
                <div className="text-red-500 text-center p-4">
                    Error loading parcels: {error.message}
                </div>
            ) : (
                <div className="flex flex-col h-full">
                    <div className="flex-1 space-y-4 overflow-y-auto">
                        <div className="grid grid-cols-2 gap-y-2 max-w-xs">
                            <div className="text-gray-500 text-left">{t('pickup.appointment.parcelPageDetails.appointmentId')}:</div>
                            <div className="text-gray-700">{displayAppointmentNumber}</div>

                            <div className="text-gray-500 text-left">{t('pickup.appointment.parcelPageDetails.parcelCount')}:</div>
                            <div className="text-gray-700">{parcelCount}</div>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={filteredParcels}
                            rowKey="trackingNumber"
                            rowSelection={rowSelection}
                            pagination={{
                                position: ['bottomRight'],
                                current: currentPage,
                                onChange: page => setCurrentPage(page),
                                pageSize: 10,
                                showSizeChanger: false,
                                showTotal: total => t('table.pagination.totalItems', { total }),
                                className: 'text-center'
                            }}
                            size="middle"
                            scroll={{x: 700}}
                        />
                    </div>

                    {/* 固定的按钮区 */}
                    <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200 flex-shrink-0">
                        <div className="text-sm text-gray-600">
                            {selectedRowKeys.length > 0 && (
                                <span>Selected {selectedRowKeys.length} item(s)</span>
                            )}
                        </div>
                        <div className="flex space-x-2">
                            <Button
                                onClick={onClose}
                                className="w-24"
                            >
                                {t('pickup.appointment.parcelPageDetails.close')}
                            </Button>
                            <Button
                                type="default"

                                onClick={showDeleteConfirm}
                                disabled={selectedRowKeys.length === 0 || currentStatus === 'DISPATCHED'}
                                loading={updateLoading}
                                className="w-24"
                                style={{
                                    borderColor: (selectedRowKeys.length === 0 || currentStatus === 'DISPATCHED') ? '#d1d5db' : '#ef4444',
                                    color: (selectedRowKeys.length === 0 || currentStatus === 'DISPATCHED') ? '#9ca3af' : '#ef4444',
                                    display:"none"
                            }}
                                onMouseEnter={(e) => {
                                    if (selectedRowKeys.length > 0 && currentStatus !== 'DISPATCHED') {
                                        e.currentTarget.style.backgroundColor = '#fef2f2';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '';
                                }}
                            >
                                {t('pickup.appointment.parcelPageDetails.delete')}
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => setAddParcelsModalVisible(true)}
                                disabled={currentStatus !== 'CREATED'}
                                className="w-24"
                            >
                                {t('pickup.appointment.parcelPageDetails.add')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* 删除确认弹框 */}
            <Modal
                title={
                    <div className="flex items-center">
                        <div className="w-1 h-6 bg-red-500 mr-2"></div>
                        <span className="text-lg font-medium">{t('pickup.appointment.parcelPageDetails.confirmDelete')}</span>
                    </div>
                }
                open={deleteConfirmVisible}
                onCancel={() => setDeleteConfirmVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setDeleteConfirmVisible(false)}>
                        {t('pickup.appointment.parcelPageDetails.cancel')}
                    </Button>,
                    <Button
                        key="delete"
                        type="default"
                        onClick={handleDeleteConfirm}
                        loading={updateLoading}
                        className="w-24"
                        style={{
                            borderColor: '#ef4444',
                            color: '#ef4444'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fef2f2';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                        }}
                    >
                        {t('pickup.appointment.parcelPageDetails.delete')}
                    </Button>,
                ]}
                width={500}
                maskClosable={!updateLoading}
                closable={!updateLoading}
            >
                <div className="flex items-start space-x-3">
                    <ExclamationCircleOutlined style={{color: '#ff4d4f', fontSize: '20px', marginTop: '2px'}}/>
                    <div>
                        <p className="text-gray-700 mb-2">
                            {t('pickup.appointment.parcelPageDetails.confirmDeleteMessage', {count: selectedRowKeys.length})}
                        </p>
                        <p className="text-gray-500 text-sm">
                            {t('pickup.appointment.parcelPageDetails.confirmDeleteMessage2')}
                        </p>
                    </div>
                </div>
            </Modal>

            {/* 添加包裹弹框 */}
            <AddParcelsModal
                visible={addParcelsModalVisible}
                onClose={() => setAddParcelsModalVisible(false)}
                appointmentId={appointmentId}
                currentTrackingNumbers={data?.getAppointmentParcels?.map(p => p.trackingNumber) || []}
                onSuccess={() => {
                    refetch();
                }}
                hubId={parseInt(warehouseId!)}
                currentChannelId={appointmentDetailData?.getPickupAppointmentDetail?.channelId || 0}
            />

            {/* 包裹详情抽屉 */}
            <CentralParcelDetailDrawer
                visible={parcelDetailVisible}
                onClose={() => setParcelDetailVisible(false)}
                orderId={selectedOrderId}
                userRole={getUserInfo()?.account?.role?.name || ''}
            />
        </Drawer>
    );
};

export default PickupAppointmentParcelsDrawer;
